import { Component } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartArea } from "@fortawesome/pro-solid-svg-icons";

import { number, rate } from "common/formats";

import { FETCH_SUMMARY_DATA } from "common/actions/dashboard";

import { ErrorView } from "common/components/error";
import { Placeholder } from "common/components/placeholder";
import { FetchErrorRetry, NoDataAvailable } from "common/helpers/errors";
import { HelpHover } from "common/components/helpHover";

import { getDateRangeAsString, formatAmountWithCurrency } from "../widgetHelper";

export class QuickSummaryWithChange extends Component {
    static processChange(current, previous) {
        const pChange = previous.primary.raw ? ((current.primary.raw - previous.primary.raw) / previous.primary.raw) * 100 : "-%";
        const pAbs = current.primary.isRate ? Math.round((previous.primary.raw - current.primary.raw) * -100) : "";
        const sChange = previous.secondary.raw ? ((current.secondary.raw - previous.secondary.raw) / previous.secondary.raw) * 100 : "-%";
        let primaryRelative;
        let secondaryRelative;
        if (pChange === "-%") {
            primaryRelative = "-%";
        } else {
            primaryRelative = pChange > 0 ? `+${rate(pChange)}` : rate(pChange);
        }
        if (sChange === "-%") {
            secondaryRelative = "-%";
        } else {
            secondaryRelative = sChange > 0 ? `+${rate(sChange)}` : rate(sChange);
        }
        return {
            primaryRelative,
            primaryAbs: pAbs ? `(${pAbs}bps)` : "",
            secondaryRelative
        };
    }
    
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            error: false
        };
    }
    
    componentDidMount() {
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    
        this.existingPromiseRequest = Promise.map(this.props.dateRanges, ({ startDate, endDate }) => FETCH_SUMMARY_DATA({
            url: this.props.subModel.get("api_endpoint")(""),
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            q: this.props.widgetQualifiers
        })).then((data) => {
            this.setState({
                loaded: true,
                error: false,
                data
            });
        }, (error) => {
            this.setState({
                loaded: true,
                error: true
            });
        });
    }
    
    componentWillUnmount() {
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    }
    
    processData(data) { // this logic needs a refactor but I need to step away from it first.
        const props = this.props;
        const reactProps = props.subModel.getClone("quick").reactProps;
        const by = props.by || "count";
        // eslint-disable-next-line no-nested-ternary
        const by2 = reactProps.chartFieldDataType === "rate" ? by : (by === "count" ? "amount" : "count");
        // I know its ugly forgive me for now ^^^^
        const field = reactProps.chartFieldToggle ? `${reactProps.chartField}_${by}` : reactProps.chartField;
        const field2 = reactProps.secondaryFieldToggle ? `${reactProps.secondaryField}_${by2}` : reactProps.secondaryField;
        
        if (data) {
            const formatVal = (dataType, val) => {
                if (dataType === "amount") {
                    return {
                        raw: val,
                        formatted: formatAmountWithCurrency(val, 10_000_000),
                        isRate: false
                    };
                }
                if (dataType === "count") {
                    return {
                        raw: val,
                        formatted: number(val),
                        isRate: false
                    };
                }
                if (dataType === "rate") {
                    return {
                        raw: val,
                        formatted: rate(val),
                        isRate: true
                    };
                }
                if (dataType === "by") {
                    return {
                        raw: val,
                        formatted: by === "count" ? number(val) : formatAmountWithCurrency(val, 10_000_000),
                        isRate: false
                    };
                }
                return {
                    raw: "Missed one",
                    formatted: "Missed one"
                };
            };
            
            const primaryFormat = reactProps.chartFieldDataType !== "by" ? reactProps.chartFieldDataType : by;
            const secondaryFormat = reactProps.secondaryFieldDataType !== "by" ? reactProps.secondaryFieldDataType : by2;
            
            return {
                primary: formatVal(primaryFormat, data[field]),
                secondary: formatVal(secondaryFormat, data[field2]),
                label: reactProps.summaryLabel
            };
        }
        return {
            primary: "",
            secondary: "",
            label: ""
        };
    }
    
    render() {
        if (this.state.loaded && this.state.error) {
            return (
                <ErrorView
                    captureInSentry={false}
                    error={new FetchErrorRetry({
                        message: "Please refresh widget as data could not load",
                        customActions: [{
                            className: "retry-widget",
                            label: "Refresh widget"
                        }],
                        hideDetails: true,
                        custom: true
                    })}
                />
            );
        }
        if (this.state.loaded && (this.state.data[0].length === 0 && this.state.data[1].length === 0)) {
            return (
                <ErrorView
                    captureInSentry={false}
                    error={new NoDataAvailable({
                        custom: true,
                        header: "No data available",
                        message: "Try expanding your date range or removing any applied filters.",
                        actions: false,
                        hideDetails: true
                    })}
                />
            );
        }
        
        const defaultData = {
            label: "",
            primary: { formatted: <QuickWidgetPlaceholder/> },
            secondary: { formatted: "" },
            primaryRelative: <QuickWidgetPlaceholder/>,
            primaryAbs: "",
            secondaryRelative: <QuickWidgetPlaceholder/>
        };
        
        const current = this.state.loaded ? this.processData(this.state.data[0][0]) : defaultData;
        const previous = this.state.loaded ? this.processData(this.state.data[1][0]) : defaultData;
        const changes = this.state.loaded ? QuickSummaryWithChange.processChange(current, previous) : defaultData;
        const changeTT = changes.primaryAbs ? "The relative and absolute change between the current and previous period." : "The relative change between the current and previous period.";
        // summaryLabel
        return (
            <div className="wrap row summary-row">
                <div className="col-xs-5 summary-block">
                    
                    <div className="primary">
                        <HelpHover size="sm" topOffset="-19px" title="Current period" body={`Data reported between ${getDateRangeAsString(this.props.dateRanges[0])}`}>
                            <div className="val"><span><FontAwesomeIcon className="icon" icon={faChartArea} size="lg" /></span> {current.primary.formatted}</div>
                        </HelpHover>
                    </div>
                    <div className="description">{current.label}</div>
                    <div className="secondary">{current.secondary.formatted}</div>
                </div>
                <div className="col-xs-7 prior-block">
                    <div className="prior-values">
                        <HelpHover size="sm" title="PRIOR PERIOD" body={`Data reported between ${getDateRangeAsString(this.props.dateRanges[1])}`}>
                            <div className="value-label">PRIOR PERIOD</div>
                        </HelpHover>
                        <div className="primary">{previous.primary.formatted}</div>
                        <div className="secondary">{previous.secondary.formatted}</div>
                    </div>
                    <div className="change">
                        <div className="title">
                            <HelpHover size="sm" title="CHANGE" body={changeTT}>
                                <div className="value-label">CHANGE</div>
                            </HelpHover>
                        </div>
                        <div className="primary">{changes.primaryRelative} {changes.primaryAbs}</div>
                        <div className="secondary">{changes.secondaryRelative}</div>
                    </div>
                </div>
            </div>
        );
    }
}

const QuickWidgetPlaceholder = styled(Placeholder)`
    display: inline-block;
    margin: 0;
    white-space: nowrap;
`;
